import { createServerFn } from "@tanstack/react-start";
import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { dossierSchema, documentSchema } from "./klantaccount-shared";

export const leesKlantaccount = createServerFn({ method: "GET" }).middleware([requireSupabaseAuth]).handler(async ({ context }) => {
  const [berichten, dossiers, documenten, installateurs, aanvragen] = await Promise.all([
    context.supabase.from("klant_berichten").select("*").eq("user_id", context.userId).order("created_at"),
    context.supabase.from("klant_dossiers").select("*").eq("user_id", context.userId).order("created_at", { ascending: false }),
    context.supabase.from("klant_documenten").select("*").eq("user_id", context.userId).order("created_at", { ascending: false }),
    context.supabase.from("klant_installateurs").select("*").eq("user_id", context.userId),
    context.supabase.from("leads").select("id,type_opdracht,status,created_at,plaats").eq("user_id", context.userId).order("created_at", { ascending: false }),
  ]);
  if ([berichten, dossiers, documenten, installateurs, aanvragen].some((r) => r.error)) throw new Error("Je account kon niet geladen worden.");
  return { userId: context.userId, berichten: berichten.data ?? [], dossiers: dossiers.data ?? [], documenten: documenten.data ?? [], installateurs: installateurs.data ?? [], aanvragen: aanvragen.data ?? [] };
});
export const klantaccountQuery = () => queryOptions({ queryKey: ["klantaccount"], queryFn: () => leesKlantaccount(), staleTime: 15_000 });

export const stuurKlantbericht = createServerFn({ method: "POST" }).middleware([requireSupabaseAuth]).inputValidator(z.object({ bericht: z.string().trim().min(1).max(4000), userId: z.string().uuid().optional() })).handler(async ({ context, data }) => {
  const userId = data.userId ?? context.userId;
  if (userId !== context.userId) {
    const rol = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    if (rol.error || !rol.data) throw new Error("Geen toegang.");
  }
  const result = await context.supabase.from("klant_berichten").insert({ user_id: userId, afzender: context.userId, bericht: data.bericht }).select("id").single();
  if (result.error) throw new Error("Je bericht is niet verzonden.");
  return result.data;
});

export const bewaarKlantdossier = createServerFn({ method: "POST" }).middleware([requireSupabaseAuth]).inputValidator(dossierSchema).handler(async ({ context, data }) => {
  if (data.inhoud.aanvraag_id) {
    const lead = await context.supabase.from("leads").select("id").eq("id", data.inhoud.aanvraag_id).eq("user_id", context.userId).maybeSingle();
    if (lead.error || !lead.data) throw new Error("Deze aanvraag hoort niet bij je account.");
  }
  const result = await context.supabase.from("klant_dossiers").insert({ ...data, user_id: context.userId }).select("id").single();
  if (result.error) throw new Error("Opslaan mislukt. Probeer het opnieuw.");
  return result.data;
});

export const leesKlantbeheer = createServerFn({ method: "GET" }).middleware([requireSupabaseAuth]).handler(async ({ context }) => {
  const rol = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
  if (rol.error || !rol.data) throw new Error("Alleen beheerders hebben toegang.");
  const [leads, berichten, dossiers, documenten, installateurs] = await Promise.all([
    context.supabase.from("leads").select("id,user_id,voornaam,achternaam,email,type_opdracht").not("user_id", "is", null).order("created_at", { ascending: false }),
    context.supabase.from("klant_berichten").select("*").order("created_at"),
    context.supabase.from("klant_dossiers").select("*").order("created_at", { ascending: false }),
    context.supabase.from("klant_documenten").select("*").order("created_at", { ascending: false }),
    context.supabase.from("klant_installateurs").select("*"),
  ]);
  if ([leads, berichten, dossiers, documenten, installateurs].some((r) => r.error)) throw new Error("Klantbeheer kon niet geladen worden.");
  return { leads: leads.data ?? [], berichten: berichten.data ?? [], dossiers: dossiers.data ?? [], documenten: documenten.data ?? [], installateurs: installateurs.data ?? [] };
});
export const klantbeheerQuery = () => queryOptions({ queryKey: ["klantbeheer"], queryFn: () => leesKlantbeheer() });

const beheerActieSchema = z.discriminatedUnion("actie", [
  z.object({ actie: z.literal("document"), document: documentSchema }),
  z.object({ actie: z.literal("dossier"), id: z.string().uuid(), status: z.enum(["ontvangen", "in_behandeling", "afgerond"]), antwoord: z.string().trim().max(4000) }),
  z.object({ actie: z.literal("installateur"), user_id: z.string().uuid(), lead_id: z.string().uuid(), bedrijfsnaam: z.string().trim().min(1).max(200), contactpersoon: z.string().trim().max(200), email: z.union([z.literal(""), z.string().email().max(254)]), telefoon: z.string().regex(/^[+0-9 ()-]*$/).max(30), toelichting: z.string().trim().max(2000) }),
]);
export const beheerKlantaccount = createServerFn({ method: "POST" }).middleware([requireSupabaseAuth]).inputValidator(beheerActieSchema).handler(async ({ context, data }) => {
  const rol = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
  if (rol.error || !rol.data) throw new Error("Alleen beheerders hebben toegang.");
  if (data.actie === "dossier") {
    const result = await context.supabase.from("klant_dossiers").update({ status: data.status, antwoord: data.antwoord }).eq("id", data.id).select("id").single();
    if (result.error) throw new Error("Bijwerken mislukt.");
    return { ok: true };
  }
  const invoer = data.actie === "document" ? data.document : data;
  const lead = await context.supabase.from("leads").select("id").eq("id", invoer.lead_id).eq("user_id", invoer.user_id).maybeSingle();
  if (lead.error || !lead.data) throw new Error("Klant en aanvraag komen niet overeen.");
  if (data.actie === "document") {
    const result = await context.supabase.from("klant_documenten").insert(data.document);
    if (result.error) throw new Error("Document plaatsen mislukt.");
  } else {
    const { actie: _actie, ...installateur } = data;
    const result = await context.supabase.from("klant_installateurs").insert(installateur);
    if (result.error) throw new Error("Installateur koppelen mislukt.");
  }
  return { ok: true };
});